import {createSelector} from 'reselect'

import {AppState} from "../index";
import {
    AdLabel,
    Ads,
    AdsFilter,
    Campaign,
    CampaignsFilter,
    CampaignWithAds,
    Organization,
    OrganizationsFilter
} from "./types";
import {IFeed} from "../feed/types";
import {ISelectOption} from "../../components/FuseSelect";
import createCachedSelector from "re-reselect";
import {IdObjectsWithId} from "../../utils";
import {union} from "lodash";

export const selectOrganizationsFilter = (state: AppState) => state.organizations.organizationsFilter;
export const selectCampaignsFilter = (state: AppState) => state.organizations.campaignsFilter;
export const selectAdsFilter = (state: AppState) => state.organizations.adsFilter;

export const selectOrganizations = (state: AppState) => state.organizations.organizations;
export const selectCampaigns = (state: AppState) => state.organizations.campaigns;
export const selectAds = (state: AppState) => state.organizations.ads;

export const selectActiveOrganization = (state: AppState) => state.organizations.organizations.byId[state.organizations.activeOrganizationId];
export const selectActiveCampaignIds = (state: AppState) => state.organizations.activeCampaignIds;
export const selectActiveAd = (state: AppState) => state.organizations.ads.byId[state.organizations.activeAdsId];
export const selectAdLabels = (state:AppState) => state.organizations.adLabels

export const selectCampaignsByActiveOrganizationId = createSelector([selectActiveOrganization, selectCampaigns], (org, campaigns): Campaign[] => {
    if (!!org && !!org.campaign_ids) {
        return org.campaign_ids.reduce((result: Campaign[], id: string) => {
            const campaign = campaigns.byId[id];
            if (campaign) {
                result.push(campaign);
            }
            return result;
        }, []).sort((a: Campaign, b: Campaign) => {
            if (a.status === "active") {
                return -1
            } else if (b.status === "active") {
                return 1
            }
            return 0
        })

    }
    return []
});

export const selectActiveCampaigns = createSelector(
    [selectActiveCampaignIds, selectCampaigns],
    (ids, campaigns): Campaign[] => {
        return ids.reduce((result: Campaign[], id: string) => {
            if (campaigns.byId[id]) {
                result.push(campaigns.byId[id])
            }
            return result
        }, [])
    });

export const createSelectAdsByCampaignId = createCachedSelector(
    selectAds,
    (state: AppState, campaign: Campaign) => campaign,
    (ads, campaign): Ads[] => {
        return Object.values(ads.byId).filter((ad) => {
            return ad.campaign_id == campaign.id
        }).sort((a: Ads, b: Ads) => {
            if (a.status === "active") {
                return -1
            } else if (b.status === "active") {
                return 1
            }
            return 0
        })
    }
)(
    (state, campaignId) => {
        return `ads_by_campaignId:${campaignId}`
    }
);


export const selectAdsByCampaigns = createCachedSelector(
    state => state,
    selectAds,
    (state: AppState, campaigns: Campaign[]) => campaigns,
    (state, ads, campaigns): IdObjectsWithId<CampaignWithAds> => {
        return campaigns.reduce((result: IdObjectsWithId<CampaignWithAds>, campaign) => {
            let x = createSelectAdsByCampaignId(state, campaign);
            result.byId[campaign.id] = {
                ...campaign,
                ads: x
            };
            return result
        }, {
            byId: {},
        });
    }
)(
    (state, ads, campaignIds) => {
        return `ads_by_campaignIds:${campaignIds}`
    }
);


export const selectCampaignWithAdsByActiveCampaigns = createCachedSelector(
    state => state,
    selectActiveCampaigns,
    (state, campaigns): IdObjectsWithId<CampaignWithAds> => {
        return selectAdsByCampaigns(state, campaigns)
    }
)(
    (state, campaignIds) => {
        return `ads_by_campaignIds:${campaignIds}`
    }
);

export const selectOrganizationState = (state: AppState) => {
    return state.organizations
};

export const selectVisibleOrganizations = createSelector(
    [selectOrganizationsFilter, selectOrganizations],
    (organizationsFilter, organizations): Organization[] => {
    switch (organizationsFilter) {
        case OrganizationsFilter.SHOW_ALL:
            return Object.values(organizations.byId).sort((a: Organization, b: Organization) => {
                if (a.status === "active") {
                    return -1
                } else if (b.status === "active") {
                    return 1
                }
                return 0
            });
        case OrganizationsFilter.SHOW_ACTIVE:
            return Object.values(organizations.byId).filter(o => o.status === "active")
    }
});

export const selectVisibleOrganizationsOptions = createSelector(
    [selectVisibleOrganizations],
    (organizations): ISelectOption[] => {
        return organizations.map((org) => {
            let sign = " ";
            if (org.status === "active") {
                sign += "✅"
            } else {
                sign += "⭕"
            }
            return {value: org.id, text: `${sign} [${org.id}] ${org.name}`, logo: org.logo}
        })
    });


export const selectVisibleCampaignsByActiveOrganizationId = createSelector(
    [selectCampaignsFilter, selectCampaignsByActiveOrganizationId],
    (campaignsFilter, campaigns): Campaign[] => {
    switch (campaignsFilter) {
        case CampaignsFilter.SHOW_ALL:
            return campaigns;
        case CampaignsFilter.SHOW_ACTIVE:
            return campaigns.filter(c => c.status === "active")
    }
});

export const selectVisibleCampaignOptions = createSelector([selectVisibleCampaignsByActiveOrganizationId],
    (campaigns): ISelectOption[] => {
        return campaigns.map((c) => {
            let sign = " ";
            if (c.status === "active") {
                sign += "✅"
            } else {
                sign += "⭕"
            }
            return {value: c.id, text: `${sign} [${c.id}] ${c.name}`}
        })
    });

export const selectVisibleAdsByActiveCampaignIds = createSelector(
    [selectAdsFilter, selectCampaignWithAdsByActiveCampaigns],
    (adsFilter, campaigns: IdObjectsWithId<CampaignWithAds>): IdObjectsWithId<CampaignWithAds> => {
    switch (adsFilter) {
        case AdsFilter.SHOW_ALL:
            return campaigns;
        case AdsFilter.SHOW_ACTIVE:
            const byIds = Object.keys(campaigns.byId).reduce((result: { [key: string]: CampaignWithAds }, id: string): { [key: string]: CampaignWithAds } => {
                result[id].ads = campaigns.byId[id].ads.filter(a => a.status === "active");
                return result
            }, {});
            return {
                byId: byIds,
            }
    }
});

export const selectAdLabelsByActiveCampaignIds = createSelector(
    [selectCampaignWithAdsByActiveCampaigns],
    (campaigns: IdObjectsWithId<CampaignWithAds>): string[] => {
        const labels = Object.keys(campaigns.byId).reduce((result: { [key: string]: boolean }, id: string) => {
            campaigns.byId[id].ads.forEach(a => {
                if (a.status === "active") {
                    result[a.label_id] = true
                }
            });
            return result
        }, {});
        return Object.keys(labels)
    });

export const selectActiveOrganizationId = (state: AppState) => {
    return state.organizations.activeOrganizationId
};

export const selectFeedById = (state: AppState, id: string): IFeed => {
    return state.organizations.feeds.byId[id]
};


export const areOrganizationsFetched = (state: AppState): boolean => {
    return Object.keys(state.organizations.organizations.byId).length > 0
};

export const areCampaignsFetched = (state: AppState): boolean => {
    const org = state.organizations.organizations.byId[state.organizations.activeOrganizationId];
    return !!org && !!org.campaign_ids && org.campaign_ids.length > 0;
};

export const areAdLabelsFetched = (state: AppState): boolean => {
    const org = state.organizations.organizations.byId[state.organizations.activeOrganizationId];
    return !!org && !!org.ad_label_ids && org.ad_label_ids.length > 0;
};

export const areFeedsFetched = (state: AppState): boolean => {
    const org = state.organizations.organizations.byId[state.organizations.activeOrganizationId];
    return !!org && !!org.feed_ids && org.feed_ids.length > 0;
};

export const areAdsFetched = createCachedSelector(
    selectAds,
    selectActiveCampaigns,
    (ads, campaigns): boolean => {
        let allFound = true;
        campaigns.forEach(c => {
            let found = false;

            c.ad_ids.forEach((adId: string) => {
                if (ads.byId[adId]) {
                    found = true;
                    return;
                }
            });
            if (!found) {
                allFound = false
            }
        });
        return allFound;
    }
)(
    (state, campaigns) => {

        return `are_ads_fetched`
    }
);
